import {
    AnalyticsEventDef,
    AuthenticationActions,
    authEntitlementsChecked,
    authLogin,
    authRefreshLogin,
    AuthTokenExchangeResponse,
    DataLayerEventName,
    debugExtendedAccess,
    Entitlements,
    ExtendedAccessEvent,
    GApi,
    getAuthStateFromUserInfoToken,
    getLoginUrl,
    GoogleEvent,
    isSwGEvent,
    LoginDetails,
    setSwGState,
    SubscribeClickOverrides,
    SubscribeWithGoogleEvent,
    Subscriptions,
    tokenExchangePath,
    UpdateEventManagerLoaded,
} from '@news-mono/web-common'
import { PromiseCompletionSource } from 'promise-completion-source'
import { Dispatch, useEffect } from 'react'
import { useDispatch } from 'react-redux'
import { Logger } from 'typescript-log'
import { debugSwg, isGoogleExtendedAccess } from '../../authentication'
import { AddToCartEvent } from '../../events'
import { GaaMeteringRegwallType } from './swg-api'

declare global {
    interface Window {
        SWG: Array<(subscriptions: any) => void>
    }
}

interface SubscribeWithGoogleProps {
    onEvent: (
        event: ExtendedAccessEvent | SubscribeWithGoogleEvent | AddToCartEvent,
    ) => void
    getSubscribeLink: (overrides?: SubscribeClickOverrides) => string
}

export const SubscribeWithGoogleContent: React.FC<SubscribeWithGoogleProps> = ({
    onEvent,
    getSubscribeLink,
}) => {
    const dispatch = useDispatch()

    useEffect(() => {
        if (getSubscribeLink) {
            SwGInitialisedPromise = SwGInitialised(
                getSubscribeLink,
                dispatch,
                onEvent,
            )
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    return null
}

export const SwGInitialised = (
    getSubscribeLink: (overrides?: SubscribeClickOverrides) => string,
    dispatch: Dispatch<any>,
    onEvent: (
        event: ExtendedAccessEvent | SubscribeWithGoogleEvent | AddToCartEvent,
    ) => void,
) =>
    new Promise<void>((resolve) => {
        ;(self.SWG = self.SWG || []).push(function (
            subscriptions: Subscriptions,
        ) {
            // Init manually https://github.com/subscriptions-project/swg-js/blob/main/docs/embed-client.md#manual-initialization
            const productId = `${
                window.location.hostname.match(/localhost$/)
                    ? 'thewest.dev.swmdigital.io'
                    : window.location.hostname.replace(/www\.|local\./, '')
            }:everyday_digital`

            debugExtendedAccess('Initialising SwG productId: %s', productId)

            subscriptions.init(productId)

            // Handle when the user clicks "Already have an account?"
            subscriptions.setOnLoginRequest(() => {
                document.location.href = getLoginUrl()
            })

            if (getSubscribeLink) {
                // Handle when the user clicks "Subscribe"
                subscriptions.setOnNativeSubscribeRequest(() => {
                    document.location.href = getSubscribeLink({
                        callToAction: 'extended-access',
                        packagePath: 'subscribe/new',
                    })
                })
            }

            subscriptions.setOnFlowCanceled((arg) =>
                debugSwg('SwG flow canceled by user %o', arg),
            )

            const GoogleEntitlementPromise =
                new PromiseCompletionSource<Entitlements>()
            subscriptions.setOnEntitlementsResponse(
                (entitlementsPromise: Promise<Entitlements>) => {
                    entitlementsPromise.then((googleEntitlement) => {
                        // Prob need to have some sort of timeout/error handling?
                        GoogleEntitlementPromise.resolve(googleEntitlement)
                    })
                },
            )

            subscriptions.getEventManager().then((manager) => {
                debugExtendedAccess('registering SwG analytics event listener')
                // Fire off eventManagerLoaded action
                dispatch(UpdateEventManagerLoaded(true))

                manager.registerEventListener((event: GoogleEvent) => {
                    const eventType =
                        event.eventType && isSwGEvent(event.eventType)
                            ? DataLayerEventName.subscribeWithGoogle
                            : DataLayerEventName.extendedAccess
                    if (
                        (eventType === DataLayerEventName.extendedAccess &&
                            isGoogleExtendedAccess()) ||
                        eventType === DataLayerEventName.subscribeWithGoogle
                    ) {
                        onEvent({
                            type: eventType,
                            payload: {
                                ...event,
                                eventType:
                                    AnalyticsEventDef[
                                        event.eventType as number
                                    ],
                            },
                            originator: 'extended-access',
                        })
                        debugExtendedAccess('analytics event: %o', event)
                    }
                })
            })

            dispatch(
                setSwGState({
                    subscriptions: subscriptions,
                    entitlementsPromise: GoogleEntitlementPromise.promise,
                }),
            )

            resolve(undefined)
        })
    })

export let SwGInitialisedPromise: Promise<void>

declare const GaaMeteringRegwall: GaaMeteringRegwallType
declare const gapi: GApi
export async function handleReturningGoogleUser(
    dispatch: Dispatch<any>,
    log: Logger,
) {
    return new Promise((resolve) => {
        try {
            gapi.load('auth2', function () {
                gapi.auth2
                    .init()
                    .then(async (GoogleAuth) => {
                        if (!GoogleAuth.isSignedIn.get()) {
                            return resolve(false)
                        }
                        const googleUser = GoogleAuth.currentUser.get()
                        const authResponse = googleUser.getAuthResponse(false)
                        if (!authResponse || !authResponse.id_token) {
                            GoogleAuth.signOut()
                            return resolve(false)
                        }
                        debugExtendedAccess(
                            'Attempting to link returning google user %o',
                            googleUser,
                        )
                        dispatch({
                            type: AuthenticationActions.GOOGLE_LOGIN,
                            payload: { googleLogin: 'linking' },
                        })
                        const data = {
                            token: authResponse.id_token,
                        }
                        await registerUser(data, dispatch, log)
                        resolve(true)
                    })
                    .catch((err) => {
                        debugExtendedAccess(
                            'Link returning google user failed %o',
                            err,
                        )
                        resolve(false)
                    })
            })
        } catch (err) {
            debugExtendedAccess('Link returning google user failed %o', err)
            resolve(false)
        }
    })
}

// Launch Google registration intervention when an anonymous user does not have access to read
// an article
export async function showGoogleRegwall(dispatch: Dispatch<any>, log: Logger) {
    return new Promise((resolve) => {
        log.info({ GaaMeteringRegwall }, 'Regwall Reached')
        const currentUrl = new URL(document.URL)
        GaaMeteringRegwall.show({
            // Specify a URL that renders a Google Sign-In button.
            // See the Google Sign-In widget section.
            iframeUrl: `${currentUrl.origin}/extended-access-login`,
        }).then(async (userData) => {
            dispatch({
                type: AuthenticationActions.GOOGLE_LOGIN,
                payload: { googleLogin: 'linking' },
            })
            // Register a user based on data from Google Sign-In.
            // Definition of googleSignInUser: https://git.io/Jkyzm
            // See Authenticate with a backend server.
            const data = {
                token: userData.idToken,
            }
            await registerUser(data, dispatch, log)
            resolve(undefined)
        })
    })
}

function completeSiteLogin(
    dispatch: Dispatch<any>,
    data: AuthTokenExchangeResponse,
    log: Logger,
) {
    const invocation = 'manual'
    const onEvent = () => null

    debugExtendedAccess('Completing site login with google sign in: %o', {
        data,
        invocation,
    })

    if (data.loginProvider === 'Google') {
        const authState: LoginDetails = {
            coralToken: data.coralToken,
            entitlements: [],
            googleUserState: data.googleUserState,
            loginProvider: data.loginProvider,
            socialProviders: '',
            subscriptionType: 'none',
            userName: `${data.userInfo.given_name} ${data.userInfo.family_name}`,
            wanUserId: data.userInfo.sub,
            hashedSophiUserID: data.hashedSophiUserID,
            hashedLiveRampEmail: data.hashedLiveRampEmail,
        }
        dispatch(authLogin({ ...authState, onEvent }))
    } else {
        dispatch(authEntitlementsChecked())
        const authState = getAuthStateFromUserInfoToken(
            data.userInfo,
            onEvent,
            data.hashedSophiUserID,
        )
        dispatch(
            authRefreshLogin({
                ...authState,
                socialProviders: 'Google',
                googleUserState: data.googleUserState,
                coralToken: data.coralToken,
            }),
        )
    }
}

async function registerUser(
    data: {
        token: string
    },
    dispatch: Dispatch<any>,
    log: Logger,
) {
    return new Promise((resolve) => {
        debugExtendedAccess('Exchanging google token for auth tokens')
        fetch(tokenExchangePath, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(data),
        })
            .then(async (response) => {
                debugExtendedAccess('Token exchange complete')
                const data = await response.json()

                dispatch({
                    type: AuthenticationActions.GOOGLE_LOGIN,
                    payload: {
                        googleLogin: 'complete',
                        googleUserState: data.googleUserState,
                    },
                })
                completeSiteLogin(dispatch, data, log)
            })
            .then(() => {
                debugExtendedAccess('Token exchange login complete')
                resolve(undefined)
            })
            .catch(function (err) {
                log.error({ err }, 'Token exchange failed')
                resolve(undefined)
            })
    })
}
